import { useState } from "react";
import { useCompany } from "../contexts/CompanyContext";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Sparkles,
  TrendingUp,
  Check,
  X,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Alert, AlertDescription } from "../components/ui/alert";
import DashboardLayout from "../components/DashboardLayout";

export function RuleSuggestions() {
  const { selectedCompanyId } = useCompany();
  const { data: selectedCompany } = trpc.company.get.useQuery(
    { id: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );
  const [creatingRuleId, setCreatingRuleId] = useState<string | null>(null);
  const [createdRules, setCreatedRules] = useState<Set<string>>(new Set());

  // Fetch rule suggestions
  const {
    data: suggestionsData,
    isLoading,
    refetch,
  } = trpc.ruleSuggestion.analyzeSuggestions.useQuery(
    {
      companyId: selectedCompany?.id!,
      limit: 100,
    },
    {
      enabled: !!selectedCompany?.id,
    }
  );

  // Create rule mutation
  const createRuleMutation =
    trpc.ruleSuggestion.createFromSuggestion.useMutation({
      onSuccess: () => {
        // Refetch suggestions after creating a rule
        refetch();
      },
    });

  const handleCreateRule = async (suggestion: any) => {
    if (!selectedCompany?.id) return;

    setCreatingRuleId(suggestion.id);

    try {
      await createRuleMutation.mutateAsync({
        companyId: selectedCompany.id,
        type: suggestion.type,
        pattern: JSON.stringify(suggestion.ruleConfig),
        category: suggestion.category,
        priority: Math.round(suggestion.confidence / 2), // Convert 0-100 to 0-50
        isActive: 1,
      });

      setCreatedRules(prev => new Set(prev).add(suggestion.id));
    } catch (error) {
      console.error("Failed to create rule:", error);
    } finally {
      setCreatingRuleId(null);
    }
  };

  const handleDismiss = (suggestionId: string) => {
    setCreatedRules(prev => new Set(prev).add(suggestionId));
  };

  if (!selectedCompany) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please select a company to view rule suggestions.
            </AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="h-8 w-8 text-accent" />
            <h1 className="text-3xl font-bold">AI Rule Suggestions</h1>
          </div>
          <p className="text-muted-foreground">
            Automatically generated categorization rules based on your
            uncategorized transactions
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading &&
          suggestionsData &&
          suggestionsData.suggestions.length === 0 && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    No Suggestions Available
                  </h3>
                  <p className="text-muted-foreground">
                    {suggestionsData.message ||
                      "All transactions are already categorized!"}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

        {/* Statistics */}
        {!isLoading &&
          suggestionsData &&
          suggestionsData.suggestions.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Analyzed Transactions</CardDescription>
                  <CardTitle className="text-3xl">
                    {suggestionsData.analyzed}
                  </CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Patterns Detected</CardDescription>
                  <CardTitle className="text-3xl">
                    {suggestionsData.patterns}
                  </CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Suggested Rules</CardDescription>
                  <CardTitle className="text-3xl">
                    {suggestionsData.suggestions.length}
                  </CardTitle>
                </CardHeader>
              </Card>
            </div>
          )}

        {/* Suggestions List */}
        {!isLoading &&
          suggestionsData &&
          suggestionsData.suggestions.length > 0 && (
            <div className="space-y-4">
              {suggestionsData.suggestions
                .filter(s => !createdRules.has(s.id))
                .map(suggestion => (
                  <Card key={suggestion.id} className="overflow-hidden">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CardTitle className="text-xl">
                              {suggestion.category}
                            </CardTitle>
                            <Badge
                              variant={
                                suggestion.confidence >= 80
                                  ? "default"
                                  : suggestion.confidence >= 60
                                    ? "secondary"
                                    : "outline"
                              }
                            >
                              {suggestion.confidence}% confidence
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {suggestion.type}
                            </Badge>
                          </div>
                          <CardDescription className="text-sm">
                            Pattern:{" "}
                            <span className="font-mono text-foreground">
                              {suggestion.pattern}
                            </span>
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleCreateRule(suggestion)}
                            disabled={creatingRuleId === suggestion.id}
                          >
                            {creatingRuleId === suggestion.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-2" />
                                Create Rule
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDismiss(suggestion.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {/* Reasoning */}
                      <div>
                        <h4 className="text-sm font-semibold mb-1">
                          AI Reasoning
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {suggestion.reasoning}
                        </p>
                      </div>

                      {/* Affected Transactions */}
                      <div>
                        <h4 className="text-sm font-semibold mb-1">
                          Affected Transactions:{" "}
                          {suggestion.affectedTransactions}
                        </h4>
                        {suggestion.examples.length > 0 && (
                          <div className="bg-muted rounded-md p-3 space-y-1">
                            {suggestion.examples
                              .slice(0, 5)
                              .map((example, idx) => (
                                <div
                                  key={idx}
                                  className="text-sm font-mono text-muted-foreground"
                                >
                                  {example}
                                </div>
                              ))}
                            {suggestion.examples.length > 5 && (
                              <div className="text-sm text-muted-foreground italic">
                                +{suggestion.examples.length - 5} more examples
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Rule Configuration Preview */}
                      <div>
                        <h4 className="text-sm font-semibold mb-1">
                          Rule Configuration
                        </h4>
                        <div className="bg-muted rounded-md p-3">
                          <pre className="text-xs font-mono overflow-x-auto">
                            {JSON.stringify(suggestion.ruleConfig, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}

        {/* Success Message */}
        {createdRules.size > 0 && (
          <Alert className="mt-6">
            <Check className="h-4 w-4" />
            <AlertDescription>
              Successfully created {createdRules.size} rule
              {createdRules.size > 1 ? "s" : ""}. Visit the{" "}
              <a href="/categorization-rules" className="underline">
                Categorization Rules
              </a>{" "}
              page to manage them.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </DashboardLayout>
  );
}
